import { STRINGS } from './strings.zh-HK';

export interface HudData {
  angleDeg: number;
  ballSpeed: number;
  state: string;
  /** Points awarded this round. */
  score: number;
  /** Points across every round since boot. */
  totalScore: number;
  /** Resolved pocket label, or null while the round is live. */
  slotLabel: string | null;
  round: number;
  /** True while the countdown is holding the ball. */
  held: boolean;
}

/**
 * The diagnostic panel.
 *
 * ---------------------------------------------------------------------------
 * THIS IS NOT THE GAME'S DISPLAY
 * ---------------------------------------------------------------------------
 * Since Phase 4 the player-facing readout is the MARQUEE - `src/ui/Display.ts`,
 * drawn over the machine's own LED panel at the top of the cabinet. That is what
 * shows the countdown, the round score and the total.
 *
 * This panel is the development overlay: board angle, ball speed, internal state
 * and the raw round counter. Those numbers are genuinely useful when something
 * looks wrong and genuinely uninteresting when nothing does, so it is styled to
 * sit out of the way in the corner rather than to be the thing the player looks
 * at. It is still plain DOM for the same reasons as before - crisp text at any
 * pixel ratio, and readable by a test.
 */
export class Hud {
  private readonly angleValue: HTMLElement;
  private readonly speedValue: HTMLElement;
  private readonly stateValue: HTMLElement;
  private readonly roundValue: HTMLElement;
  private readonly scoreValue: HTMLElement;
  private readonly totalValue: HTMLElement;
  private readonly slotValue: HTMLElement;
  private readonly heldValue: HTMLElement;
  private readonly hintsEl: HTMLElement;

  constructor(root: HTMLElement) {
    root.innerHTML = '';

    const panel = document.createElement('div');
    panel.className = 'hud-panel';

    const title = document.createElement('div');
    title.className = 'hud-title';
    title.textContent = STRINGS.hud.title;
    panel.appendChild(title);

    this.roundValue = Hud.addRow(panel, STRINGS.hud.round, '1');
    this.scoreValue = Hud.addRow(panel, STRINGS.hud.score, '0');
    this.totalValue = Hud.addRow(panel, STRINGS.hud.totalScore, '0');
    this.slotValue = Hud.addRow(panel, STRINGS.hud.slot, STRINGS.slotPending);
    this.heldValue = Hud.addRow(panel, STRINGS.hud.held, '—');
    this.angleValue = Hud.addRow(panel, STRINGS.hud.angle, '0°');
    this.speedValue = Hud.addRow(panel, STRINGS.hud.ballSpeed, '0.00 m/s');
    this.stateValue = Hud.addRow(panel, STRINGS.hud.state, STRINGS.state.loading);

    this.hintsEl = document.createElement('ul');
    this.hintsEl.className = 'hud-hints';
    panel.appendChild(this.hintsEl);

    root.appendChild(panel);

    // Device-aware copy: a touch device gets the mobile controls, a pointer
    // device keeps the keyboard/mouse lines. Primary input can change at runtime
    // (attach a keyboard, plug in a mouse), so the media query watches for it.
    const coarse =
      typeof window.matchMedia === 'function' ? window.matchMedia('(pointer: coarse)') : null;
    this.renderHints(coarse?.matches ?? false);
    coarse?.addEventListener('change', (event) => this.renderHints(event.matches));
  }

  /** Fill the hint list for a coarse (touch) or fine (pointer) primary input. */
  private renderHints(coarse: boolean): void {
    const lines = coarse ? STRINGS.hintsTouch : STRINGS.hintsDesktop;
    this.hintsEl.replaceChildren(
      ...lines.map((text) => {
        const item = document.createElement('li');
        item.textContent = text;
        return item;
      }),
    );
  }

  private static addRow(parent: HTMLElement, label: string, initial: string): HTMLElement {
    const row = document.createElement('div');
    row.className = 'hud-row';

    const labelEl = document.createElement('span');
    labelEl.className = 'hud-label';
    labelEl.textContent = label;

    const valueEl = document.createElement('span');
    valueEl.className = 'hud-value';
    valueEl.textContent = initial;

    row.append(labelEl, valueEl);
    parent.appendChild(row);
    return valueEl;
  }

  update(data: HudData): void {
    // The angle is intentionally unbounded - it is shown exactly as accumulated,
    // so a player who spins the board three times sees 1080 degrees.
    const rounded = Math.round(data.angleDeg);
    const sign = rounded > 0 ? '+' : '';
    this.angleValue.textContent = `${sign}${rounded}°`;

    this.speedValue.textContent = `${data.ballSpeed.toFixed(2)} m/s`;
    this.stateValue.textContent = data.state;

    this.roundValue.textContent = String(data.round);
    this.scoreValue.textContent = String(data.score);
    this.totalValue.textContent = String(data.totalScore);
    this.slotValue.textContent = data.slotLabel ?? STRINGS.slotPending;
    this.heldValue.textContent = data.held ? STRINGS.state.countdown : '—';
  }

  /**
   * Show a blocking banner when one or more production assets failed to load.
   *
   * The game keeps running on procedural geometry, but it must not look as
   * though nothing went wrong.
   */
  showAssetFailure(messages: readonly string[]): void {
    if (messages.length === 0) return;

    const banner = document.createElement('div');
    banner.className = 'hud-asset-error';

    const heading = document.createElement('div');
    heading.className = 'hud-asset-error-title';
    heading.textContent = STRINGS.assetError;
    banner.appendChild(heading);

    for (const message of messages) {
      const line = document.createElement('div');
      line.className = 'hud-asset-error-line';
      line.textContent = message;
      banner.appendChild(line);
    }

    document.body.appendChild(banner);
  }
}
